/**
 * Admin · QR-Kampagne (/erleben) Editor
 * --------------------------------------
 * Bearbeitet 5 `site_content`-Keys: erleben.hero / .headline / .price /
 * .pillars / .contact. Save/Verwerfen/Live-Vorschau identisch zu
 * AdminStartseite.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactElement } from "react";
import {
    Save, Check, ExternalLink, AlertCircle,
    Layers, Sparkles, Lightbulb, Package, LayoutGrid, Mail,
    FileText, MessageCircle, ListChecks, Flag,
} from "lucide-react";
import { api } from "@/lib/auth";
import type { MediaRef } from "@/lib/media";
import { invalidateSiteContentCache } from "@/lib/siteContent";
import {
    SectionCard, TextField, TextareaField, SelectField, MediaField, RepeaterField, ToggleField,
} from "@/components/admin/editor/fields";
import MediaPicker, { type PickerContext } from "@/components/admin/MediaPicker";
import RichTextField from "@/components/admin/RichTextField";
import TabNavigation, { type Tab } from "@/components/admin/TabNavigation";
import SectionEnabledToggle from "@/components/admin/SectionEnabledToggle";

const ALL_TAB_ID = "all";

type KeyName = "erleben.hero" | "erleben.headline" | "erleben.intro" | "erleben.usp"
    | "erleben.pillars" | "erleben.benefits" | "erleben.price" | "erleben.closing" | "erleben.contact";
const KEYS: KeyName[] = [
    "erleben.headline", "erleben.hero", "erleben.intro", "erleben.usp",
    "erleben.price", "erleben.pillars", "erleben.benefits", "erleben.closing",
    "erleben.contact",
];

const META: Record<KeyName, { title: string; anchor: string; description: string }> = {
    "erleben.hero":     { title: "Hero",         anchor: "hero",     description: "Slider mit beliebig vielen Medien. Type wird automatisch erkannt (Foto · 360°-Foto · Video · 360°-Video)." },
    "erleben.headline": { title: "Headline",     anchor: "headline", description: "Zentrierte Editorial-Überschrift + Untertitel." },
    "erleben.intro":    { title: "Intro-Absatz", anchor: "intro",    description: "Editorial-Absatz mit Drohnen-Highlight-Callout." },
    "erleben.usp":      { title: "USP-Absatz",   anchor: "usp",      description: "Kurzer Satz zur Alleinstellung (freies Umsehen)." },
    "erleben.pillars":  { title: "Säulen",       anchor: "pillars",  description: "Drei Zielgruppen-Karten (Repeater: Icon, Titel, Text, CTA, Kategorie)." },
    "erleben.benefits": { title: "Warum HypeFly?", anchor: "benefits", description: "Vorteils-Liste mit dezenten Gold-Check-Icons." },
    "erleben.price":    { title: "Preis-Banner", anchor: "price",    description: "Einstiegs-Preis-Aussage." },
    "erleben.closing":  { title: "Closing-Claim", anchor: "closing", description: "Finaler Serifen-Claim + Kontakt-Button." },
    "erleben.contact":  { title: "Kontakt",      anchor: "contact",  description: "Überschrift + Untertitel + Empfänger-E-Mail." },
};

const TAB_ICONS: Record<string, ReactElement> = {
    hero: <Sparkles size={14} />,
    headline: <Lightbulb size={14} />,
    intro: <FileText size={14} />,
    usp: <MessageCircle size={14} />,
    pillars: <LayoutGrid size={14} />,
    benefits: <ListChecks size={14} />,
    price: <Package size={14} />,
    closing: <Flag size={14} />,
    contact: <Mail size={14} />,
};

const ICON_CHOICES = [
    { value: "Home", label: "Home (Familien)" },
    { value: "HardHat", label: "HardHat (Neubau)" },
    { value: "Sprout", label: "Sprout (Generation)" },
    { value: "TreeDeciduous", label: "TreeDeciduous (Erbe)" },
    { value: "Users", label: "Users (Gemeinschaft)" },
];

type Sections = Partial<Record<KeyName, Record<string, unknown>>>;

function deepEqual(a: unknown, b: unknown): boolean {
    return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/** Legacy-Helper (nicht mehr aktiv genutzt — bleibt für zukünftige Migrationen). */
function refFromId(id: string): MediaRef {
    return { media_id: id, path: "" } as MediaRef;
}
void refFromId;

/**
 * Master-Switch-Card ganz oben im Editor. Steuert `erleben.enabled` (CMS-
 * Key `erleben` mit Value `{ enabled: boolean }`). Bei AUS:
 *   - Route `/erleben` redirected via `<ErlebenGate>` nach `/`.
 *   - Header-Nav-Punkt "Erleben" wird ausgeblendet.
 *   - Alle Per-Section-Toggles bleiben unangetastet und wirken bei Wieder-
 *     einschalten sofort weiter.
 */
function MasterSwitchCard({
    enabled, onChange, dirty,
}: {
    enabled: boolean;
    onChange: (next: boolean) => void;
    dirty: boolean;
}) {
    return (
        <section
            className="p-5 md:p-6 rounded-md"
            style={{
                background: enabled
                    ? "linear-gradient(180deg, hsl(var(--hf-surface)) 0%, hsl(var(--hf-bg)) 100%)"
                    : "hsl(var(--hf-danger) / 0.06)",
                border: `1.5px solid ${enabled ? "hsl(var(--hf-gold))" : "hsl(var(--hf-danger) / 0.4)"}`,
                boxShadow: "var(--shadow-warm-sm)",
            }}
            data-testid="erleben-master-switch-card"
        >
            <div className="flex items-start md:items-center justify-between gap-4 flex-wrap md:flex-nowrap">
                <div className="min-w-0">
                    <div className="hf-eyebrow" style={{ fontSize: 10 }}>
                        Sektion 0 · Sichtbarkeit {dirty && (
                            <span className="ml-2" style={{ color: "hsl(var(--hf-gold))" }}>· ungespeichert</span>
                        )}
                    </div>
                    <h2 className="font-display text-xl md:text-2xl leading-tight mt-0.5">
                        Erleben-Seite {enabled ? "aktiv" : "ausgeschaltet"}
                    </h2>
                    <p className="text-xs md:text-sm mt-2 max-w-2xl" style={{ color: "hsl(var(--hf-muted))" }}>
                        Master-Switch für die komplette Route <code>/erleben</code>. Bei <strong>AUS</strong>{" "}
                        wird der Header-Nav-Punkt &bdquo;Erleben&ldquo; auf allen Seiten ausgeblendet und die Route
                        leitet automatisch zur Startseite um. Die Sektions-Toggles unten bleiben unverändert
                        und werden beim erneuten Aktivieren sofort wieder wirksam.
                    </p>
                </div>
                <label
                    className="inline-flex items-center gap-3 cursor-pointer shrink-0 select-none"
                    data-testid="erleben-master-toggle-label"
                >
                    <span
                        className="text-xs uppercase tracking-[0.14em]"
                        style={{ color: enabled ? "hsl(var(--hf-gold))" : "hsl(var(--hf-danger))", fontWeight: 700 }}
                    >
                        {enabled ? "AN" : "AUS"}
                    </span>
                    <button
                        type="button"
                        role="switch"
                        aria-checked={enabled}
                        onClick={() => onChange(!enabled)}
                        data-testid="erleben-master-toggle"
                        className="relative inline-block w-14 h-8 rounded-full transition-colors focus:outline-none focus-visible:ring-2"
                        style={{
                            background: enabled ? "hsl(var(--hf-gold))" : "hsl(var(--hf-line))",
                        }}
                    >
                        <span
                            className="absolute top-1 left-1 w-6 h-6 rounded-full transition-transform"
                            style={{
                                background: "#FFFDF9",
                                transform: enabled ? "translateX(24px)" : "translateX(0)",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                            }}
                        />
                    </button>
                </label>
            </div>
        </section>
    );
}

export default function AdminQRKampagnePage() {
    const [saved, setSaved] = useState<Sections>({});
    const [edit, setEdit] = useState<Sections>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [flash, setFlash] = useState<"ok" | "err" | null>(null);
    const [flashMsg, setFlashMsg] = useState("");
    const [pickerCtx, setPickerCtx] = useState<PickerContext>(null);
    // Master-Switch für /erleben. Eigener kleiner State neben den Section-
    // Keys, weil `erleben` (ohne Punkt-Suffix) kein Repeater-Section-Key
    // ist sondern ein simples { enabled: boolean }-Objekt. Wird via
    // `useErleben()` auf der Live-Site + im Route-Guard konsumiert.
    const [savedMasterEnabled, setSavedMasterEnabled] = useState<boolean>(true);
    const [editMasterEnabled, setEditMasterEnabled] = useState<boolean>(true);
    const masterDirty = savedMasterEnabled !== editMasterEnabled;

    const tabIdFor = (k: string): string => k.split(".").pop() ?? k;
    const tabs = useMemo<Tab[]>(() => {
        const sectionTabs: Tab[] = KEYS.map((k) => {
            const id = tabIdFor(k);
            return { id, label: META[k].title, icon: TAB_ICONS[id] };
        });
        return [{ id: ALL_TAB_ID, label: "Alle", icon: <Layers size={15} /> }, ...sectionTabs];
    }, []);

    const readHash = (): string | null => {
        if (typeof window === "undefined") return null;
        const h = window.location.hash.replace(/^#/, "");
        return h && tabs.some((t) => t.id === h) ? h : null;
    };
    const [activeTab, setActiveTab] = useState<string>(() => readHash() ?? ALL_TAB_ID);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const desired = `#${activeTab}`;
        if (window.location.hash !== desired) window.history.replaceState(null, "", desired);
    }, [activeTab]);
    useEffect(() => {
        const onHash = () => {
            if (typeof window === "undefined") return;
            const h = window.location.hash.replace(/^#/, "");
            if (h && tabs.some((t) => t.id === h) && h !== activeTab) setActiveTab(h);
        };
        window.addEventListener("hashchange", onHash);
        return () => window.removeEventListener("hashchange", onHash);
    }, [activeTab, tabs]);

    /* ---- Load ---- */
    useEffect(() => {
        void (async () => {
            // Prefix `erleben` (ohne Punkt) → matcht sowohl `erleben` (Master-
            // Switch) als auch `erleben.*` (die 9 Sections). Der Master-Key
            // steckt danach separat in `items["erleben"]`.
            const res = await api<{ items: Record<string, unknown> }>(
                "/api/site-content?prefix=erleben",
            );
            const items = res.ok && res.data ? res.data.items : {};
            const s: Sections = {};
            for (const k of KEYS) {
                const v = items[k];
                s[k] = (v && typeof v === "object" && !Array.isArray(v))
                    ? (v as Record<string, unknown>) : {};
            }
            setSaved(s);
            setEdit(JSON.parse(JSON.stringify(s)));
            // Master-Switch: default true (Backwards-Compat: fehlender Key
            // = aktiv). Nur explizites `enabled: false` blendet aus.
            const master = items["erleben"] as { enabled?: unknown } | undefined;
            const masterOn = !master || typeof master !== "object"
                ? true
                : (master as { enabled?: boolean }).enabled !== false;
            setSavedMasterEnabled(masterOn);
            setEditMasterEnabled(masterOn);
            setLoading(false);
        })();
    }, []);

    const changedKeys = KEYS.filter((k) => !deepEqual(saved[k], edit[k]));
    const hasChanges = changedKeys.length > 0 || masterDirty;

    useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            if (!hasChanges) return;
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [hasChanges]);

    const setSection = useCallback((key: KeyName, value: Record<string, unknown>) => {
        setEdit((prev) => ({ ...prev, [key]: value }));
    }, []);

    /* ---- Save ---- */
    const doSave = useCallback(async () => {
        if (!hasChanges || saving) return;
        setSaving(true);
        setFlash(null);
        const errors: string[] = [];
        const nextSaved: Sections = { ...saved };
        for (const k of changedKeys) {
            const res = await api(`/api/admin/site-content/${encodeURIComponent(k)}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ value: edit[k] ?? {} }),
            });
            if (res.ok) {
                nextSaved[k] = JSON.parse(JSON.stringify(edit[k] ?? {}));
            } else {
                errors.push(`${k}: ${res.error || "unknown"}`);
            }
        }
        // Master-Switch persistieren, wenn geändert.
        let nextMaster = savedMasterEnabled;
        if (masterDirty) {
            const mRes = await api(`/api/admin/site-content/${encodeURIComponent("erleben")}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ value: { enabled: editMasterEnabled } }),
            });
            if (mRes.ok) {
                nextMaster = editMasterEnabled;
            } else {
                errors.push(`erleben: ${mRes.error || "unknown"}`);
            }
        }
        setSaved(nextSaved);
        setSavedMasterEnabled(nextMaster);
        setSaving(false);
        invalidateSiteContentCache();
        const totalChanges = changedKeys.length + (masterDirty ? 1 : 0);
        if (errors.length) {
            setFlash("err");
            setFlashMsg(`Fehler bei ${errors.length}/${totalChanges}: ${errors.join(" · ")}`);
        } else {
            setFlash("ok");
            setFlashMsg(`Gespeichert (${totalChanges} ${totalChanges === 1 ? "Änderung" : "Änderungen"})`);
            setTimeout(() => setFlash(null), 2000);
        }
    }, [hasChanges, saving, saved, changedKeys, edit, masterDirty, editMasterEnabled, savedMasterEnabled]);

    const doDiscard = useCallback(() => {
        if (!hasChanges || saving) return;
        if (!confirm("Alle Änderungen verwerfen?")) return;
        setEdit(JSON.parse(JSON.stringify(saved)));
        setEditMasterEnabled(savedMasterEnabled);
    }, [hasChanges, saving, saved, savedMasterEnabled]);

    /* ---- Picker ---- */
    const openPicker = useCallback((
        currentType?: string | string[],
        onPick?: (id: string, path: string) => void,
    ) => {
        setPickerCtx({
            type: currentType,
            onPick: (mediaId, path) => {
                onPick?.(mediaId, path);
                setPickerCtx(null);
            },
        });
    }, []);

    if (loading) return <div className="text-sm text-hf-muted">Lade…</div>;

    const dirtyTabIds = new Set<string>();
    for (const k of KEYS) if (!deepEqual(saved[k], edit[k])) dirtyTabIds.add(tabIdFor(k));

    const activeKey = KEYS.find((k) => tabIdFor(k) === activeTab) ?? KEYS[0];
    const activeMeta = META[activeKey];

    return (
        <div data-testid="admin-qr-kampagne">
            <div className="sticky top-0 z-40 -mx-6 md:-mx-8 mb-8 backdrop-blur"
                style={{ background: "hsl(var(--hf-bg) / 0.94)" }}>
                <div className="px-6 md:px-8 py-4 flex items-center justify-between"
                    style={{ borderBottom: "1px solid hsl(var(--hf-line))" }}>
                    <div>
                        <div className="hf-eyebrow" style={{ fontSize: 10 }}>Admin › Erleben-Landing (QR-Kampagne)</div>
                        <h1 className="font-display text-2xl md:text-3xl leading-tight mt-0.5">Erleben-Landing (QR-Kampagne) bearbeiten</h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <a href="/erleben" target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs md:text-sm px-3 py-2 rounded-sm border"
                            style={{ borderColor: "hsl(var(--hf-line))", background: "hsl(var(--hf-surface))" }}
                            data-testid="editor-preview-link">
                            <ExternalLink size={14} /> Live-Vorschau
                        </a>
                        <button type="button" onClick={doDiscard} disabled={!hasChanges || saving}
                            data-testid="editor-discard-button"
                            className="text-xs md:text-sm px-3 py-2 rounded-sm border disabled:opacity-30"
                            style={{ borderColor: "hsl(var(--hf-line))" }}>Verwerfen</button>
                        <button type="button" onClick={doSave} disabled={!hasChanges || saving}
                            data-testid="editor-save-button"
                            className="inline-flex items-center gap-2 text-xs md:text-sm px-4 py-2 rounded-sm font-medium disabled:opacity-40"
                            style={{ background: hasChanges ? "hsl(var(--hf-gold))" : "hsl(var(--hf-line))", color: "hsl(var(--hf-ink))" }}>
                            {flash === "ok" ? <Check size={14} /> : <Save size={14} />}
                            <span>{saving ? "Speichere…" : hasChanges ? `Speichern · ${changedKeys.length + (masterDirty ? 1 : 0)}` : "Gespeichert"}</span>
                        </button>
                    </div>
                </div>
                <TabNavigation
                    tabs={tabs}
                    activeId={activeTab}
                    onChange={setActiveTab}
                    dirtyIds={dirtyTabIds}
                    testId="admin-qr-kampagne-tabs"
                    ariaLabel="Erleben-Landing-Sektionen"
                    allTabId={ALL_TAB_ID}
                />
            </div>

            {flash === "err" && (
                <div className="mb-6 p-4 rounded-sm text-sm flex items-start gap-2"
                    style={{ background: "hsl(var(--hf-danger) / 0.12)", color: "hsl(var(--hf-danger))" }}>
                    <AlertCircle size={16} className="mt-0.5" /> {flashMsg}
                </div>
            )}

            <div role="tabpanel" id={`tabpanel-${activeTab}`} aria-labelledby={`tab-${activeTab}`} className="space-y-6">
                {activeTab === ALL_TAB_ID ? (
                    <>
                        <MasterSwitchCard
                            enabled={editMasterEnabled}
                            onChange={setEditMasterEnabled}
                            dirty={masterDirty}
                        />
                        <section
                            className="p-5 md:p-6 rounded-md"
                            style={{
                                background: "hsl(var(--hf-surface))",
                                border: "1px solid hsl(var(--hf-line))",
                                boxShadow: "var(--shadow-warm-sm)",
                            }}
                            data-testid="all-sections-overview"
                        >
                            <div className="flex items-baseline justify-between mb-3">
                                <div>
                                    <div className="hf-eyebrow" style={{ fontSize: 10 }}>Übersicht</div>
                                    <h2 className="font-display text-xl md:text-2xl leading-tight mt-0.5">Alle Sektionen · Sichtbarkeit</h2>
                                </div>
                                <span
                                    className="text-[11px]"
                                    style={{ color: "hsl(var(--hf-muted))" }}
                                    data-testid="all-sections-overview-hint"
                                >
                                    {KEYS.filter((k) => (edit[k] as { enabled?: boolean } | undefined)?.enabled === false).length} von {KEYS.length} ausgeblendet
                                </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {KEYS.map((k) => (
                                    <SectionEnabledToggle
                                        key={k}
                                        enabled={(edit[k] as { enabled?: boolean } | undefined)?.enabled}
                                        onChange={(next) => setSection(k, { ...(edit[k] ?? {}), enabled: next })}
                                        label={META[k].title}
                                        testId={`overview-toggle-${tabIdFor(k)}`}
                                    />
                                ))}
                            </div>
                        </section>
                        {KEYS.map((k) => (
                            <SectionCard key={k} sectionKey={k}
                                title={META[k].title} description={META[k].description}
                                anchor={META[k].anchor} dirty={!deepEqual(saved[k], edit[k])}>
                                <SectionEnabledToggle
                                    enabled={(edit[k] as { enabled?: boolean } | undefined)?.enabled}
                                    onChange={(next) => setSection(k, { ...(edit[k] ?? {}), enabled: next })}
                                    testId={`panel-toggle-${tabIdFor(k)}`}
                                    hint={k === "erleben.hero"
                                        ? "Ohne Hero verliert die QR-Kampagne den primären Blickfang – nur ausschalten wenn absichtlich Text-only-Variante gewünscht."
                                        : k === "erleben.contact"
                                        ? "Die Kontakt-Sektion wird aktuell nicht auf /erleben gerendert – Toggle ist vorbereitet für spätere Freischaltung."
                                        : undefined}
                                />
                                <SectionEditor sectionKey={k} value={edit[k]}
                                    onChange={(v) => setSection(k, v)} openPicker={openPicker} />
                            </SectionCard>
                        ))}
                    </>
                ) : (
                    <SectionCard key={activeKey} sectionKey={activeKey}
                        title={activeMeta.title} description={activeMeta.description}
                        anchor={activeMeta.anchor} dirty={!deepEqual(saved[activeKey], edit[activeKey])}>
                        <SectionEnabledToggle
                            enabled={(edit[activeKey] as { enabled?: boolean } | undefined)?.enabled}
                            onChange={(next) => setSection(activeKey, { ...(edit[activeKey] ?? {}), enabled: next })}
                            testId={`panel-toggle-${tabIdFor(activeKey)}`}
                            hint={activeKey === "erleben.hero"
                                ? "Ohne Hero verliert die QR-Kampagne den primären Blickfang – nur ausschalten wenn absichtlich Text-only-Variante gewünscht."
                                : activeKey === "erleben.contact"
                                ? "Die Kontakt-Sektion wird aktuell nicht auf /erleben gerendert – Toggle ist vorbereitet für spätere Freischaltung."
                                : undefined}
                        />
                        <SectionEditor sectionKey={activeKey} value={edit[activeKey]}
                            onChange={(v) => setSection(activeKey, v)} openPicker={openPicker} />
                    </SectionCard>
                )}
            </div>

            <MediaPicker ctx={pickerCtx} onClose={() => setPickerCtx(null)} />
        </div>
    );
}

/* ---------------- Section Editors ---------------- */
function SectionEditor({
    sectionKey, value, onChange, openPicker,
}: {
    sectionKey: KeyName;
    value: Record<string, unknown> | undefined;
    onChange: (v: Record<string, unknown>) => void;
    openPicker: (currentType?: string | string[], onPick?: (id: string, path: string) => void) => void;
}) {
    const v = (value ?? {}) as Record<string, unknown>;
    const set = (k: string, x: unknown) => onChange({ ...v, [k]: x });
    const getStr = (k: string) => String(v[k] ?? "");

    switch (sectionKey) {
        case "erleben.hero": {
            type HeroSlide = { id?: string; media?: MediaRef; label?: string; enabled?: boolean };
            const slides: HeroSlide[] = Array.isArray(v.items) ? (v.items as HeroSlide[]) : [];
            // Legacy-Erkennung: alte 2-Slot-Felder — falls Migration noch nicht lief.
            const legacyVid = getStr("video_media_id").trim();
            const legacyPhoto = getStr("photo_media_id").trim();
            const hasLegacy = !!(legacyVid || legacyPhoto);
            const adoptLegacy = () => {
                const built: HeroSlide[] = [];
                if (legacyVid) built.push({
                    id: `eh${Date.now()}-v`, media: { media_id: legacyVid, path: "" },
                    label: getStr("labelLeft") || "", enabled: true,
                });
                if (legacyPhoto) built.push({
                    id: `eh${Date.now()}-p`, media: { media_id: legacyPhoto, path: "" },
                    label: getStr("labelRight") || "", enabled: true,
                });
                onChange({
                    ...v,
                    items: [...built, ...slides],
                    video_media_id: null,
                    photo_media_id: null,
                    labelLeft: null,
                    labelRight: null,
                });
            };
            return (
                <>
                    {hasLegacy && slides.length === 0 && (
                        <div
                            className="p-3 rounded-sm text-[12px] flex items-center justify-between gap-3"
                            style={{
                                background: "hsl(var(--hf-gold-soft))",
                                border: "1px solid hsl(var(--hf-gold-dark) / 0.4)",
                                color: "hsl(var(--hf-gold-dark))",
                            }}
                            data-testid="erleben-hero-legacy-migrate-banner"
                        >
                            <span>
                                Alte 2-Slot-Felder (Video/Foto) erkannt. Übernehmen, um sie als
                                Slides ins neue Multi-Item-Hero zu importieren.
                            </span>
                            <button
                                type="button"
                                onClick={adoptLegacy}
                                className="text-[11px] px-3 py-1.5 rounded-sm uppercase tracking-[0.14em] font-medium"
                                style={{
                                    background: "hsl(var(--hf-gold))",
                                    color: "hsl(var(--hf-ink))",
                                    border: "1px solid hsl(var(--hf-gold-dark))",
                                }}
                                data-testid="erleben-hero-legacy-migrate-btn"
                            >
                                In Slides übernehmen
                            </button>
                        </div>
                    )}
                    <RepeaterField<HeroSlide>
                        label="Hero-Slides (Multi-Item)"
                        items={slides}
                        onChange={(next) => set("items", next)}
                        newItem={() => ({ id: `eh${Date.now()}`, media: null, label: "", enabled: true })}
                        testId="erleben-hero-items"
                        renderItem={(it, i, upd) => (
                            <div className="space-y-3">
                                <MediaField
                                    label={`Slide #${i + 1} · Medium`}
                                    mediaRef={it.media as MediaRef}
                                    onChange={(m) => upd({ ...it, media: m })}
                                    openPicker={openPicker}
                                    mediaType={["photo", "photo_360", "video", "video_360"]}
                                    testId={`erleben-hero-item-${i}-media`}
                                    helpText="Zulässig: Foto · 360°-Foto · Video · 360°-Video · Type wird automatisch erkannt und passend gerendert."
                                />
                                <TextField
                                    label="Untertitel (optional)"
                                    value={it.label || ""}
                                    onChange={(x) => upd({ ...it, label: x })}
                                    testId={`erleben-hero-item-${i}-label`}
                                    placeholder="z.B. 360° · Luftaufnahme"
                                />
                            </div>
                        )}
                    />
                </>
            );
        }

        case "erleben.headline":
            return (
                <>
                    <TextField label="H1 · Haupt-Claim" value={getStr("title")}
                        onChange={(x) => set("title", x)} testId="headline-title" />
                    <RichTextField label="Lead-Text (Jost muted, emotionaler Rahmen)" value={getStr("subtitle")}
                        onChange={(x) => set("subtitle", x)} testId="headline-subtitle" rows={3} />
                    <RichTextField label="Body-Text (Jost Ink, konkrete User-Experience)" value={getStr("body")}
                        onChange={(x) => set("body", x)} testId="headline-body" rows={4} />
                </>
            );

        case "erleben.intro": {
            const paras = Array.isArray(v.bodyParagraphs)
                ? (v.bodyParagraphs as string[]).map(String)
                : (v.body ? [String(v.body)] : []);
            return (
                <>
                    <TextField
                        label="Headline (Fraunces)"
                        value={getStr("headline")}
                        onChange={(x) => set("headline", x)}
                        testId="intro-headline"
                    />
                    <RepeaterField<string>
                        label="Editorial-Absätze (Fließtext, Reihenfolge steuert Rendering)"
                        items={paras}
                        onChange={(next) => onChange({
                            ...v,
                            bodyParagraphs: next,
                            body: undefined, // Legacy-Feld leeren
                        })}
                        newItem={() => ""}
                        minItems={1}
                        maxItems={6}
                        testId="intro-body-paragraphs"
                        renderItem={(item, idx, upd) => (
                            <TextareaField
                                label={`Absatz #${idx + 1}`}
                                value={item}
                                onChange={upd}
                                testId={`intro-body-p-${idx}`}
                            />
                        )}
                    />
                    <TextField
                        label="Highlight-Box · Eyebrow"
                        value={getStr("highlightEyebrow")}
                        onChange={(x) => set("highlightEyebrow", x)}
                        testId="intro-highlight-eyebrow"
                    />
                    <TextField
                        label="Highlight-Box · Headline"
                        value={getStr("highlightHeadline")}
                        onChange={(x) => set("highlightHeadline", x)}
                        testId="intro-highlight-headline"
                    />
                    {(() => {
                        const hpParas = Array.isArray(v.highlightBodyParagraphs)
                            ? (v.highlightBodyParagraphs as string[]).map(String)
                            : (v.highlightBody ? [String(v.highlightBody)] : []);
                        return (
                            <RepeaterField<string>
                                label="Highlight-Box · Body-Absätze"
                                items={hpParas}
                                onChange={(next) => onChange({
                                    ...v,
                                    highlightBodyParagraphs: next,
                                    highlightBody: undefined, // Legacy-Feld leeren
                                })}
                                newItem={() => ""}
                                minItems={1}
                                maxItems={6}
                                testId="intro-highlight-body-paragraphs"
                                renderItem={(item, idx, upd) => (
                                    <TextareaField
                                        label={`Absatz #${idx + 1}`}
                                        value={item}
                                        onChange={upd}
                                        testId={`intro-highlight-body-p-${idx}`}
                                    />
                                )}
                            />
                        );
                    })()}
                </>
            );
        }

        case "erleben.usp":
            return (
                <>
                    <RichTextField
                        label="USP-Text"
                        value={getStr("body")}
                        onChange={(x) => set("body", x)}
                        testId="usp-body"
                        rows={3}
                    />
                </>
            );

        case "erleben.benefits": {
            const items = Array.isArray(v.items)
                ? (v.items as string[]).map(String)
                : [];
            return (
                <>
                    <TextField
                        label="Überschrift"
                        value={getStr("heading")}
                        onChange={(x) => set("heading", x)}
                        testId="benefits-heading"
                    />
                    <RepeaterField<string>
                        label="Vorteile (Bullet-Liste, Gold-Häkchen)"
                        items={items}
                        onChange={(next) => set("items", next)}
                        newItem={() => ""}
                        minItems={1}
                        maxItems={8}
                        testId="benefits-items"
                        renderItem={(item, idx, upd) => (
                            <TextField
                                label={`Vorteil #${idx + 1}`}
                                value={item}
                                onChange={upd}
                                testId={`benefits-item-${idx}`}
                            />
                        )}
                    />
                </>
            );
        }

        case "erleben.closing":
            return (
                <>
                    <TextField
                        label="Finaler Claim (Serifen-Schrift)"
                        value={getStr("claim")}
                        onChange={(x) => set("claim", x)}
                        testId="closing-claim"
                    />
                    <TextField
                        label="CTA-Text (Button)"
                        value={getStr("cta")}
                        onChange={(x) => set("cta", x)}
                        testId="closing-cta"
                    />
                </>
            );

        case "erleben.price":
            return (
                <>
                    <TextField label="Kleiner Eyebrow-Text" value={getStr("eyebrow")}
                        onChange={(x) => set("eyebrow", x)} testId="price-eyebrow" />
                    <TextField label="Preis-Aussage" value={getStr("text")}
                        onChange={(x) => set("text", x)} testId="price-text" />
                    <TextField label="Untertitel" value={getStr("subtitle")}
                        onChange={(x) => set("subtitle", x)} testId="price-subtitle" />
                </>
            );

        case "erleben.pillars": {
            const items = Array.isArray(v.items) ? (v.items as Array<Record<string, unknown>>) : [];
            return (
                <RepeaterField<Record<string, unknown>>
                    label="Säulen · 3 Zielgruppen-Karten"
                    items={items}
                    onChange={(next) => set("items", next)}
                    newItem={() => ({
                        id: `id-${Date.now()}`, icon: "Home",
                        title: "", body: "", cta: "Unverbindlich anfragen →", category: "",
                        enabled: true,
                    })}
                    minItems={1} maxItems={6}
                    testId="pillars-repeater"
                    renderItem={(item, _idx, upd) => {
                        const it = item;
                        return (
                            <>
                                <ToggleField
                                    label="Sichtbar auf /erleben"
                                    value={it.enabled !== false}
                                    onChange={(v) => upd({ ...it, enabled: v })}
                                    testId={`pillar-enabled-${it.id ?? "x"}`}
                                    helpText="Aus → diese Sparte wird komplett aus dem Frontend ausgeblendet."
                                />
                                <TextField label="ID (intern)" value={String(it.id ?? "")}
                                    onChange={(x) => upd({ ...it, id: x })} />
                                <SelectField label="Icon" value={String(it.icon ?? "Home")}
                                    onChange={(x) => upd({ ...it, icon: x })} options={ICON_CHOICES} />
                                <TextField label="Titel" value={String(it.title ?? "")}
                                    onChange={(x) => upd({ ...it, title: x })} />
                                <RichTextField label="Beschreibungstext" value={String(it.body ?? "")}
                                    onChange={(x) => upd({ ...it, body: x })} rows={3} testId={`benefit-body-${it.id ?? "x"}`} />
                                <TextField label="CTA-Text" value={String(it.cta ?? "")}
                                    onChange={(x) => upd({ ...it, cta: x })} />
                                <TextField
                                    label="Kategorie (wird ins Kontaktformular übernommen)"
                                    value={String(it.category ?? "")}
                                    onChange={(x) => upd({ ...it, category: x })} />
                            </>
                        );
                    }}
                />
            );
        }

        case "erleben.contact":
            return (
                <>
                    <TextField label="Überschrift" value={getStr("heading")}
                        onChange={(x) => set("heading", x)} testId="contact-heading" />
                    <TextField label="Untertitel" value={getStr("subheading")}
                        onChange={(x) => set("subheading", x)} testId="contact-subheading" />
                    <TextField label="Empfänger-E-Mail (mailto-Fallback)"
                        value={getStr("recipient_email")}
                        onChange={(x) => set("recipient_email", x)}
                        testId="contact-recipient" />
                </>
            );

        default:
            return <div className="text-sm text-hf-muted">Unbekannte Section.</div>;
    }
}
